import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useWebSocket } from "@/hooks/useWebSocket";
import {
  HEADPHONE_PROFILES, STANDARD_RANGE_PROFILE, PHONE_DAC_PROFILES,
  matchHeadphoneProfile, detectPhoneBrand, detectAudioOutputDevices,
  gainFromDbMeterCalibration, gainFromHeadphoneProfile,
} from "@/data/headphoneProfiles";

const BRAND_COLOR = "#1e3a6e";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ─── Web Audio Tone Generator ───
// Three-tier calibration system for clinical accuracy:
//
// TIER 1 (Best): Phone dB Meter Calibration (iPhone/Samsung)
//   → Play reference tone → user reads phone's headphone dB meter → lock anchor
//   → All levels calculated from this objective measurement
//
// TIER 2: Known Headphone Profile
//   → Auto-detect headphone via Bluetooth name (enumerateDevices)
//   → Apply lab-measured sensitivity + frequency correction curve
//   → User sets volume to specific level (70%)
//
// TIER 3: Standard Range (Fallback)
//   → Use median consumer TWS specs (104 dB/mW, 16Ω)
//   → User sets volume to 70%
//   → Consistent calibration that doesn't include hearing loss bias

// RETSPL: Reference Equivalent Threshold Sound Pressure Levels
// These define 0 dB HL (normal hearing threshold) in dB SPL at each frequency.
// Source: ANSI S3.6-2018
const RETSPL_SUPRA = {
  250: 25.5, 500: 11.5, 1000: 7.0, 2000: 9.0,
  3000: 10.0, 4000: 9.5, 6000: 15.5, 8000: 13.0,
};
const RETSPL_INSERT = {
  250: 17.5, 500: 9.5, 1000: 5.5, 2000: 3.0,
  3000: 3.5, 4000: 5.5, 6000: 2.0, 8000: 0.0,
};

// The dB HL level that the "comfortable" calibration tone represents.
const CALIB_REF_DB_HL = 40;

class ToneGenerator {
  constructor() {
    this.audioContext = null;
    this.oscillator = null;
    this.gainNode = null;
    this.isPlaying = false;
    this.transducerType = "insert";
    this.keepAliveOsc = null;
    this.keepAliveGain = null;

    // === Calibration state ===
    // calibrationTier: 1 (dB meter), 2 (headphone profile), 3 (standard range)
    this.calibrationTier = null;
    
    // Tier 1: dB meter anchor
    this.dbMeterRefGain = null;   // gain used during reference tone
    this.dbMeterRefSPL = null;    // SPL reported by phone's meter

    // Tier 2/3: headphone profile
    this.headphoneProfile = null; // { sensitivity_db_mw, impedance, freq_correction }
    this.dacVrms = null;          // estimated DAC output voltage

    // Legacy: Comfortable-level calibration (kept as additional fallback)
    this.userZeroDbHlGain = null;

    // === Auto-init Tier 3 as absolute safety net ===
    // This ensures physics-based gain calculation is ALWAYS available,
    // even if the device setup flow is somehow bypassed.
    this._initDefaultCalibration();
  }

  async init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
    return this.audioContext.state === "running";
  }

  startKeepAlive() {
    if (this.keepAliveOsc || !this.audioContext) return;
    try {
      this.keepAliveOsc = this.audioContext.createOscillator();
      this.keepAliveGain = this.audioContext.createGain();
      this.keepAliveGain.gain.setValueAtTime(0, this.audioContext.currentTime);
      this.keepAliveOsc.frequency.setValueAtTime(200, this.audioContext.currentTime);
      this.keepAliveOsc.connect(this.keepAliveGain);
      this.keepAliveGain.connect(this.audioContext.destination);
      this.keepAliveOsc.start();
    } catch (e) {
      console.warn("KeepAlive start error:", e);
    }
  }

  stopKeepAlive() {
    if (this.keepAliveOsc) {
      try { this.keepAliveOsc.stop(); } catch (e) {}
      this.keepAliveOsc = null;
      this.keepAliveGain = null;
    }
  }

  async ensureRunning() {
    if (!this.audioContext) {
      await this.init();
      return;
    }
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  getRetspl(frequency) {
    const table = this.transducerType === "supra_aural" ? RETSPL_SUPRA : RETSPL_INSERT;
    if (table[frequency] !== undefined) return table[frequency];
    const freqs = Object.keys(table).map(Number).sort((a, b) => a - b);
    if (frequency <= freqs[0]) return table[freqs[0]];
    if (frequency >= freqs[freqs.length - 1]) return table[freqs[freqs.length - 1]];
    for (let i = 0; i < freqs.length - 1; i++) {
      if (frequency >= freqs[i] && frequency <= freqs[i + 1]) {
        const ratio = (frequency - freqs[i]) / (freqs[i + 1] - freqs[i]);
        return table[freqs[i]] + ratio * (table[freqs[i + 1]] - table[freqs[i]]);
      }
    }
    return table[1000] || 5.5;
  }

  /**
   * Set calibration from comfortable-level measurement.
   * The user adjusts a 1kHz tone to "clearly comfortable" — this maps to 40 dB HL.
   * 0 dB HL gain = comfortableGain / 10^(40/20) = comfortableGain / 100.
   *
   * This does NOT compensate for hearing loss (unlike "barely audible" calibration),
   * because the "comfortable" level anchors to an absolute perceptual reference.
   */
  // ── Tier 1: dB Meter Calibration ──
  setDbMeterCalibration(refGain, measuredSPL) {
    this.calibrationTier = 1;
    this.dbMeterRefGain = refGain;
    this.dbMeterRefSPL = measuredSPL;
  }

  // ── Tier 2: Headphone Profile Calibration ──
  setHeadphoneProfileCalibration(profile, dacVrms) {
    this.calibrationTier = 2;
    this.headphoneProfile = profile;
    this.dacVrms = dacVrms;
  }

  // ── Tier 3: Standard Range Calibration ──
  setStandardRangeCalibration(dacVrms) {
    this.calibrationTier = 3;
    this.headphoneProfile = STANDARD_RANGE_PROFILE;
    this.dacVrms = dacVrms;
  }

  // ── Auto-init default Tier 3 calibration ──
  // Uses conservative assumptions: standard consumer earbuds + generic phone DAC.
  // This is the safety net so we NEVER play unscaled tones.
  _initDefaultCalibration() {
    this.headphoneProfile = STANDARD_RANGE_PROFILE;
    // Conservative DAC estimate: ~0.40 Vrms at 70% volume (typical midrange phone)
    this.dacVrms = 0.40;
    // Don't set calibrationTier here — it will be overridden by device setup.
    // But the profile+dacVrms ensure the physics-based path is always reachable.
  }

  // ── Legacy: comfortable-level calibration (used if no tier set) ──
  setCalibration(gainAtComfortable) {
    const safeGain = Math.max(0.0001, Math.min(gainAtComfortable, 1.0));
    this.userZeroDbHlGain = safeGain / Math.pow(10, CALIB_REF_DB_HL / 20);
    if (!this.calibrationTier) this.calibrationTier = 0; // legacy
  }

  /**
   * Convert dB HL to Web Audio linear gain.
   * Dispatches to the appropriate tier-based calculation.
   * Uses ISO 389 RETSPL + headphone/DAC physics for all tiers.
   */
  dbHLToGain(dbHL, frequency) {
    const retspl = this.transducerType === "supra_aural" ? RETSPL_SUPRA : RETSPL_INSERT;

    if (this.calibrationTier === 1 && this.dbMeterRefGain && this.dbMeterRefSPL) {
      // Tier 1: physics from dB meter anchor
      const retsplVal = retspl[frequency] ?? retspl[1000] ?? 5.5;
      const targetSPL = dbHL + retsplVal;
      return Math.min(Math.max(
        gainFromDbMeterCalibration(targetSPL, this.dbMeterRefSPL, this.dbMeterRefGain),
        0), 1.0);
    }

    if ((this.calibrationTier === 2 || this.calibrationTier === 3)
        && this.headphoneProfile && this.dacVrms) {
      // Tier 2/3: physics from headphone profile
      return gainFromHeadphoneProfile(
        dbHL, frequency, this.headphoneProfile, this.dacVrms, retspl
      );
    }

    // ── Fallback: ALWAYS use physics-based calculation ──
    // Even if no calibration tier was explicitly set, use the default
    // Tier 3 profile (auto-initialized in constructor) for safe output.
    if (this.headphoneProfile && this.dacVrms) {
      return gainFromHeadphoneProfile(
        dbHL, frequency, this.headphoneProfile, this.dacVrms, retspl
      );
    }

    // Absolute last-resort: should never reach here, but if it does,
    // use STANDARD_RANGE_PROFILE with conservative DAC estimate.
    return gainFromHeadphoneProfile(
      dbHL, frequency, STANDARD_RANGE_PROFILE, 0.40, retspl
    );
  }

  setTransducer(type) {
    this.transducerType = type === "over_ear" || type === "supra_aural" ? "supra_aural" : "insert";
  }

  /**
   * Route audio to a specific ear using ChannelMergerNode.
   * More reliable than StereoPanner on iOS Safari + Bluetooth earbuds.
   * ChannelMerger input 0 = LEFT, input 1 = RIGHT.
   */
  _routeToEar(sourceNode, ear) {
    const merger = this.audioContext.createChannelMerger(2);
    if (ear === "left") {
      sourceNode.connect(merger, 0, 0); // mono → left channel only
    } else if (ear === "right") {
      sourceNode.connect(merger, 0, 1); // mono → right channel only
    } else {
      // "both" — send to both channels
      sourceNode.connect(merger, 0, 0);
      sourceNode.connect(merger, 0, 1);
    }
    merger.connect(this.audioContext.destination);
    return merger;
  }

  /** Play a continuous tone (for calibration). Returns cleanup function. */
  async playContinuous(frequency, gain, ear = "both") {
    const ready = await this.init();
    if (!ready) return () => {};
    this.stop();
    try {
      this.oscillator = this.audioContext.createOscillator();
      this.oscillator.type = "sine";
      this.oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.setValueAtTime(gain, this.audioContext.currentTime);
      this.oscillator.connect(this.gainNode);
      this._currentMerger = this._routeToEar(this.gainNode, ear);
      this.oscillator.start();
      this.isPlaying = true;
      return () => this.stop();
    } catch (e) {
      return () => {};
    }
  }

  /** Update gain of currently playing tone (for calibration slider) */
  updateGain(gain) {
    if (this.gainNode && this.isPlaying) {
      this.gainNode.gain.setValueAtTime(Math.max(0, Math.min(gain, 1)), this.audioContext.currentTime);
    }
  }

  async playTone(frequency, durationMs = 1000, decibelHL = 40, ear = "right", masking = null) {
    const ready = await this.init();
    if (!ready) return false;
    this.stop();
    this.stopMasking(); // Stop any previous masking noise
    try {
      this.oscillator = this.audioContext.createOscillator();
      this.oscillator.type = "sine";
      this.oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      this.gainNode = this.audioContext.createGain();
      const gain = this.dbHLToGain(decibelHL, frequency);
      // Safety cap: never let gain exceed ~85 dB SPL equivalent (protect hearing)
      const MAX_SAFE_GAIN = 0.15; // ~85 dB SPL with AirPods at 70%
      const safeGain = Math.min(gain, MAX_SAFE_GAIN);
      // Debug: log gain values for calibration validation
      const dBFS = safeGain > 0 ? (20 * Math.log10(safeGain)).toFixed(1) : "-Inf";
      console.log(`[HearClear] Tone: ${frequency}Hz @ ${decibelHL}dB HL | Tier ${this.calibrationTier || '?'} | gain=${safeGain.toExponential(3)} (${dBFS} dBFS) | ear=${ear}${gain > MAX_SAFE_GAIN ? ' [CAPPED]' : ''}`);
      const t = this.audioContext.currentTime;
      const dur = durationMs / 1000;
      // Smooth envelope: 20ms rise, hold, 20ms fall (prevents click artifacts)
      this.gainNode.gain.setValueAtTime(0, t);
      this.gainNode.gain.linearRampToValueAtTime(safeGain, t + 0.02);
      this.gainNode.gain.setValueAtTime(safeGain, t + dur - 0.02);
      this.gainNode.gain.linearRampToValueAtTime(0, t + dur);
      // Route to correct ear using ChannelMergerNode (reliable on iOS Safari)
      this.oscillator.connect(this.gainNode);
      this._currentMerger = this._routeToEar(this.gainNode, ear);
      this.oscillator.start(t);
      this.oscillator.stop(t + dur);
      this.isPlaying = true;
      setTimeout(() => { this.isPlaying = false; }, durationMs);

      // ── MASKING: Play narrowband noise to non-test ear simultaneously ──
      if (masking && masking.ear && masking.decibel != null) {
        this._playMaskingNoise(masking.frequency || frequency, masking.decibel, masking.ear, dur);
      }

      return true;
    } catch (e) {
      console.error("Tone playback error:", e);
      return false;
    }
  }

  /** Play narrowband noise (masking) to a specific ear for a given duration */
  _playMaskingNoise(centerFreq, decibelHL, ear, durationSec) {
    try {
      const t = this.audioContext.currentTime;
      // Create white noise buffer
      const sampleRate = this.audioContext.sampleRate;
      const bufferSize = Math.ceil(sampleRate * (durationSec + 0.1));
      const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Narrowband filter: bandpass centered on test frequency (1/3 octave bandwidth)
      const bandpass = this.audioContext.createBiquadFilter();
      bandpass.type = "bandpass";
      bandpass.frequency.setValueAtTime(centerFreq, t);
      bandpass.Q.setValueAtTime(3.0, t); // ~1/3 octave bandwidth

      // Gain for masking level
      const maskGain = this.audioContext.createGain();
      const gain = this.dbHLToGain(decibelHL, centerFreq);
      const safeMaskGain = Math.min(gain, 0.15); // Safety cap for masking too
      maskGain.gain.setValueAtTime(0, t);
      maskGain.gain.linearRampToValueAtTime(safeMaskGain, t + 0.02);
      maskGain.gain.setValueAtTime(safeMaskGain, t + durationSec - 0.02);
      maskGain.gain.linearRampToValueAtTime(0, t + durationSec);

      // Route masking noise to correct ear using ChannelMergerNode
      noiseSource.connect(bandpass);
      bandpass.connect(maskGain);
      this._routeToEar(maskGain, ear);

      noiseSource.start(t);
      noiseSource.stop(t + durationSec + 0.05);
      this._maskingSource = noiseSource;
    } catch (e) {
      console.error("Masking noise error:", e);
    }
  }

  /** Stop any active masking noise */
  stopMasking() {
    if (this._maskingSource) {
      try { this._maskingSource.stop(); } catch (e) {}
      this._maskingSource = null;
    }
  }

  stop() {
    if (this.oscillator) {
      try { this.oscillator.stop(); } catch (e) {}
      this.oscillator = null;
    }
    if (this._currentMerger) {
      try { this._currentMerger.disconnect(); } catch (e) {}
      this._currentMerger = null;
    }
    this.isPlaying = false;
  }

  /** Full cleanup — stops everything and closes AudioContext */
  destroy() {
    this.stop();
    this.stopMasking();
    this.stopKeepAlive();
    if (this.audioContext) {
      try { this.audioContext.close(); } catch (e) {}
      this.audioContext = null;
    }
    this.userZeroDbHlGain = null;
  }

  /** Suspend AudioContext (for when browser goes to background) */
  suspend() {
    if (this.audioContext && this.audioContext.state === "running") {
      // Suspend context — this pauses ALL audio nodes (oscillators, keepalive)
      // without destroying them. They resume automatically when context resumes.
      this.audioContext.suspend().catch(() => {});
    }
  }
}

// ─── Constants ───
const PHASES = {
  LOADING: "loading",
  ERROR: "error",
  WELCOME: "welcome",
  HEADPHONE_CHECK: "headphone_check",
  NOISE_CHECK: "noise_check",
  DEVICE_SETUP: "device_setup", // NEW: Tiered calibration setup
  JOINING: "joining",
  WAITING: "waiting",
  INSTRUCTIONS: "instructions",
  CALIBRATION: "calibration",
  TESTING: "testing",
  COMPLETED: "completed",
};

// ─── Mini Patient Audiogram (Canvas-based, works in both dark and light mode) ───
const AUDIOGRAM_FREQS = [250, 500, 1000, 2000, 4000, 8000];
const fmtF = (f) => (f >= 1000 ? `${f / 1000}k` : f);

function MiniPatientAudiogram({ thresholds, light = false }) {
  const canvasRef = useRef(null);
  const right = thresholds?.right || {};
  const left = thresholds?.left || {};

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 300, H = 140;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const PAD_L = 28, PAD_R = 8, PAD_T = 8, PAD_B = 18;
    const plotW = W - PAD_L - PAD_R;
    const plotH = H - PAD_T - PAD_B;
    const dbMin = -10, dbMax = 120, dbRange = dbMax - dbMin;

    const freqX = (f) => PAD_L + (AUDIOGRAM_FREQS.indexOf(f) / (AUDIOGRAM_FREQS.length - 1)) * plotW;
    const dbY = (db) => PAD_T + ((db - dbMin) / dbRange) * plotH;

    ctx.fillStyle = light ? "#ffffff" : "#1a1a2e";
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = light ? "#e5e7eb" : "#2a2a3e";
    ctx.lineWidth = 0.3;
    [0, 20, 40, 60, 80, 100, 120].forEach((db) => {
      const y = dbY(db);
      ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(W - PAD_R, y); ctx.stroke();
    });
    AUDIOGRAM_FREQS.forEach((f) => {
      const x = freqX(f);
      ctx.beginPath(); ctx.moveTo(x, PAD_T); ctx.lineTo(x, H - PAD_B); ctx.stroke();
    });

    // Normal line at 25 dB
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 0.8;
    ctx.setLineDash([3, 3]);
    const ny = dbY(25);
    ctx.beginPath(); ctx.moveTo(PAD_L, ny); ctx.lineTo(W - PAD_R, ny); ctx.stroke();
    ctx.setLineDash([]);

    // Y labels
    ctx.fillStyle = light ? "#9ca3af" : "#6b7280";
    ctx.font = "8px sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    [0, 40, 80, 120].forEach((db) => { ctx.fillText(String(db), PAD_L - 3, dbY(db)); });

    // X labels
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    AUDIOGRAM_FREQS.forEach((f) => { ctx.fillText(fmtF(f), freqX(f), H - PAD_B + 3); });

    const plotEar = (earData, color, marker) => {
      const pts = AUDIOGRAM_FREQS.map((f) => {
        const t = earData[f] ?? earData[String(f)];
        if (t === undefined || t === null) return null;
        return { x: freqX(f), y: dbY(t) };
      }).filter(Boolean);
      if (pts.length === 0) return;
      if (pts.length > 1) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        pts.forEach((p, i) => { i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y); });
        ctx.stroke();
      }
      const r = 3;
      pts.forEach((p) => {
        if (marker === "circle") {
          ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = light ? "#fff" : "#1a1a2e";
          ctx.fill();
          ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();
        } else {
          ctx.strokeStyle = color; ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(p.x - r, p.y - r); ctx.lineTo(p.x + r, p.y + r);
          ctx.moveTo(p.x + r, p.y - r); ctx.lineTo(p.x - r, p.y + r);
          ctx.stroke();
        }
      });
    };

    plotEar(right, "#ef4444", "circle");
    plotEar(left, "#3b82f6", "x");
  }, [thresholds, light, right, left]);

  return (
    <div className="flex justify-center">
      <canvas ref={canvasRef} style={{ width: 300, height: 140 }} className="rounded-lg" />
    </div>
  );
}

// ─── Main Component ───
export default function PTAPatientPage() {
  const { sessionCode } = useParams();
  const [searchParams] = useSearchParams();
  const autoStart = searchParams.get("autostart") === "true";
  const [phase, setPhase] = useState(PHASES.LOADING);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [isSelfTest, setIsSelfTest] = useState(false);
  const [aiStarted, setAiStarted] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const [testPaused, setTestPaused] = useState(false);

  // ── Device detection & calibration tier state ──
  const [detectedPhone, setDetectedPhone] = useState(null); // key from PHONE_DAC_PROFILES
  const [detectedHeadphone, setDetectedHeadphone] = useState(null); // { label, profile, profileKey }
  const [calibrationTier, setCalibrationTier] = useState(null); // 1, 2, or 3
  const [dbMeterReading, setDbMeterReading] = useState(""); // user-entered SPL from phone's dB meter
  const [deviceSetupStep, setDeviceSetupStep] = useState("detecting"); // detecting | volume_set | db_meter | confirm
  const REF_TONE_GAIN = 0.05; // Known gain for the reference tone during Tier 1 calibration
  const TARGET_VOLUME_PERCENT = 70; // Ask user to set phone volume to this %

  // Tone state — CRITICAL: NO visual state that leaks tone timing to patient
  const [currentToneId, setCurrentToneId] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [responseFlash, setResponseFlash] = useState(false); // Brief "Recorded" confirmation
  // Track internally whether a response was submitted for the current tone
  const responseSubmittedRef = useRef(false);
  // Track response count internally (not displayed)
  const responseCountRef = useRef(0);

  const toneGen = useRef(new ToneGenerator());
  const lastToneIdRef = useRef(null);
  const toneStartRef = useRef(null);
  const pollRef = useRef(null);
  const hasActiveSessionRef = useRef(false);

  // Noise check state
  const [noiseLevel, setNoiseLevel] = useState(null); // dB SPL estimate
  const [noiseStatus, setNoiseStatus] = useState(null); // "checking" | "pass" | "fail"
  const noiseStreamRef = useRef(null);
  const noiseAnalyserRef = useRef(null);
  const noiseCheckTimerRef = useRef(null);

  const NOISE_THRESHOLD_DB = 50; // Max acceptable ambient noise (dB SPL estimate)

  // Headphone detection state
  const [headphoneStatus, setHeadphoneStatus] = useState(null); // "checking" | "detected" | "not_detected"
  const [headphoneDeviceName, setHeadphoneDeviceName] = useState("");

  // ─── Headphone check ───
  const startHeadphoneCheck = async () => {
    setPhase(PHASES.HEADPHONE_CHECK);
    setHeadphoneStatus("checking");
    try {
      // Need a brief getUserMedia call first — browsers require this before enumerateDevices returns labels
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      tempStream.getTracks().forEach((t) => t.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter((d) => d.kind === "audiooutput");

      // Look for non-default, non-speaker audio outputs (headphones, AirPods, etc.)
      const externalDevices = audioOutputs.filter((d) => {
        const label = (d.label || "").toLowerCase();
        const isBuiltIn =
          label.includes("speaker") ||
          label.includes("built-in") ||
          label.includes("internal") ||
          label.includes("earpiece") ||
          d.deviceId === "default" ||
          d.deviceId === "";
        return !isBuiltIn && label.length > 0;
      });

      if (externalDevices.length > 0) {
        setHeadphoneStatus("detected");
        setHeadphoneDeviceName(externalDevices[0].label || "External Audio Device");
      } else {
        // Check if there are ANY audiooutput devices besides default
        const nonDefault = audioOutputs.filter(
          (d) => d.deviceId !== "default" && d.deviceId !== "" && d.deviceId !== "communications"
        );
        if (nonDefault.length > 1) {
          // Multiple outputs suggest headphones might be connected
          setHeadphoneStatus("detected");
          setHeadphoneDeviceName("Audio device");
        } else {
          setHeadphoneStatus("not_detected");
        }
      }
    } catch (err) {
      // If we can't detect, ask user to confirm manually
      console.warn("Device enumeration failed:", err);
      setHeadphoneStatus("not_detected");
    }
  };

  // ─── Fetch session ───
  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pta-live-sessions/code/${sessionCode}`);
      setSession(res.data);
      setError(null);
      hasActiveSessionRef.current = true;
      return res.data;
    } catch (err) {
      // Don't kill an active test over a single network hiccup
      if (!hasActiveSessionRef.current) {
        setError(err.response?.data?.detail || "Session not found. Please check your code.");
        setPhase(PHASES.ERROR);
      }
      return null;
    }
  }, [sessionCode]);

  // ─── Initial load ───
  useEffect(() => {
    if (!sessionCode) return;
    fetchSession().then((s) => {
      if (s) {
        setIsSelfTest(!!s.is_self_test || autoStart);
        if (s.status === "completed") setPhase(PHASES.COMPLETED);
        else if (s.status === "created") setPhase(PHASES.WELCOME);
        else setPhase(mapStatusToPhase(s.status));
      }
    });
  }, [sessionCode, fetchSession, autoStart]);

  // ─── WebSocket: instant tone delivery (bonus — does NOT replace polling) ──
  const handlePatientWs = useCallback((data) => {
    if (data.type === "tone") {
      // Instant tone received via WebSocket — play immediately
      if (data.tone_id && data.tone_id !== lastToneIdRef.current) {
        lastToneIdRef.current = data.tone_id;
        setCurrentToneId(data.tone_id);
        responseSubmittedRef.current = false;
        toneStartRef.current = Date.now();
        const ear = data.ear || "right";
        const freq = data.frequency || 1000;
        const db = data.decibel != null ? data.decibel : 40;
        toneGen.current.playTone(freq, 1000, db, ear, data.masking || null).catch(console.error);
      }
    } else if (data.type === "status") {
      const newPhase = mapStatusToPhase(data.status);
      setPhase(newPhase);
      fetchSession();
    } else if (data.type === "completed") {
      setPhase(PHASES.COMPLETED);
      fetchSession();
    }
  }, [fetchSession]);

  const wsPath = session?.session_id ? `/api/ws/patient/${session.session_id}` : null;
  useWebSocket(wsPath, handlePatientWs, {
    enabled: !!session?.session_id && phase !== PHASES.LOADING && phase !== PHASES.ERROR && phase !== PHASES.WELCOME && phase !== PHASES.HEADPHONE_CHECK && phase !== PHASES.NOISE_CHECK && phase !== PHASES.CALIBRATION,
  });

  // ─── Polling: ALWAYS runs at 800ms for reliable tone detection on all devices ───
  // WebSocket provides instant delivery as a bonus, but polling is the reliable baseline.
  // Mobile browsers (especially in-app browsers) have unreliable WebSocket support.
  useEffect(() => {
    if (phase === PHASES.LOADING || phase === PHASES.ERROR || phase === PHASES.WELCOME || phase === PHASES.HEADPHONE_CHECK || phase === PHASES.NOISE_CHECK || phase === PHASES.DEVICE_SETUP || phase === PHASES.CALIBRATION) return;

    pollRef.current = setInterval(async () => {
      const s = await fetchSession();
      if (!s) return;

      const newPhase = mapStatusToPhase(s.status);
      setPhase(newPhase);

      // Detect new tone (primary detection method on mobile)
      if (s.current_tone_id && s.current_tone_id !== lastToneIdRef.current) {
        lastToneIdRef.current = s.current_tone_id;
        setCurrentToneId(s.current_tone_id);
        responseSubmittedRef.current = false;
        toneStartRef.current = Date.now();

        // Play the tone on patient's device — NO VISUAL CHANGE
        const ear = s.current_ear || "right";
        const freq = s.current_frequency || 1000;
        const db = s.current_decibel != null ? s.current_decibel : 40;
        toneGen.current.playTone(freq, 1000, db, ear, s.current_masking || null).catch(console.error);
      }
    }, 800);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [phase, fetchSession]);

  function mapStatusToPhase(status) {
    const map = {
      created: PHASES.WELCOME,
      patient_joined: PHASES.WAITING,
      instructions: PHASES.INSTRUCTIONS,
      calibration: PHASES.CALIBRATION,
      testing_right: PHASES.TESTING,
      testing_left: PHASES.TESTING,
      completed: PHASES.COMPLETED,
      cancelled: PHASES.ERROR,
    };
    return map[status] || PHASES.WAITING;
  }

  // ─── Noise check ───
  const startNoiseCheck = async () => {
    setPhase(PHASES.NOISE_CHECK);
    setNoiseStatus("checking");
    setNoiseLevel(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      noiseStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      noiseAnalyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.fftSize);
      let maxRms = 0;
      let readings = 0;

      const measure = () => {
        if (!noiseAnalyserRef.current) return;
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        // Convert RMS to approximate dB SPL (rough mapping for mobile mics)
        const dbEstimate = Math.max(20, Math.round(20 * Math.log10(Math.max(rms, 0.0001)) + 90));
        setNoiseLevel(dbEstimate);
        if (rms > maxRms) maxRms = rms;
        readings++;
        if (readings < 20) {
          noiseCheckTimerRef.current = requestAnimationFrame(measure);
        } else {
          // After ~20 frames (~0.6-1s), evaluate
          const finalDb = Math.max(20, Math.round(20 * Math.log10(Math.max(maxRms, 0.0001)) + 90));
          setNoiseLevel(finalDb);
          stopNoiseCheck();
          if (finalDb <= NOISE_THRESHOLD_DB) {
            setNoiseStatus("pass");
            // Proceed to device setup (tiered calibration)
            setTimeout(() => startDeviceSetup(), 800);
          } else {
            setNoiseStatus("fail");
          }
        }
      };
      // Small delay for mic to stabilize
      setTimeout(() => { noiseCheckTimerRef.current = requestAnimationFrame(measure); }, 300);
    } catch (err) {
      // Mic access denied — skip noise check, go to device setup
      console.warn("Mic access denied, skipping noise check:", err);
      stopNoiseCheck();
      startDeviceSetup();
    }
  };

  const stopNoiseCheck = () => {
    if (noiseCheckTimerRef.current) cancelAnimationFrame(noiseCheckTimerRef.current);
    if (noiseStreamRef.current) {
      noiseStreamRef.current.getTracks().forEach((t) => t.stop());
      noiseStreamRef.current = null;
    }
    noiseAnalyserRef.current = null;
  };

  // ─── Device Setup & Tiered Calibration ───
  const startDeviceSetup = async () => {
    setPhase(PHASES.DEVICE_SETUP);
    setDeviceSetupStep("detecting");

    // 1. Detect phone brand
    const phoneBrand = detectPhoneBrand();
    setDetectedPhone(phoneBrand);
    const phoneProfile = PHONE_DAC_PROFILES[phoneBrand] || PHONE_DAC_PROFILES.generic;

    // 2. Detect connected headphones (we already have mic permission from noise check)
    let headphone = null;
    try {
      const outputs = await detectAudioOutputDevices();
      // Pick the first output with a recognized profile, or the first non-default output
      const matched = outputs.find(o => o.profile);
      const anyOutput = outputs.find(o => o.label && !o.label.toLowerCase().includes("default") && !o.label.toLowerCase().includes("speaker"));
      headphone = matched || anyOutput || null;
    } catch (e) {
      console.warn("Headphone detection failed:", e);
    }
    setDetectedHeadphone(headphone);

    // 3. Determine calibration tier
    if (phoneProfile.has_db_meter) {
      // Tier 1: iPhone or Samsung → can use phone's dB meter
      setCalibrationTier(1);
      setDeviceSetupStep("volume_set");
    } else if (headphone?.profile) {
      // Tier 2: Known headphone profile
      setCalibrationTier(2);
      setDeviceSetupStep("volume_set");
    } else {
      // Tier 3: Standard range
      setCalibrationTier(3);
      setDeviceSetupStep("volume_set");
    }
  };

  // After user sets volume to 70%, move to next step
  const confirmVolumeSet = async () => {
    const phoneProfile = PHONE_DAC_PROFILES[detectedPhone] || PHONE_DAC_PROFILES.generic;

    if (calibrationTier === 1) {
      // Tier 1: Play reference tone + ask user to read dB meter
      setDeviceSetupStep("db_meter");
      const ready = await toneGen.current.init();
      if (session?.headphone_type) toneGen.current.setTransducer(session.headphone_type);
      toneGen.current.startKeepAlive();
      // Play reference tone at known gain
      await toneGen.current.playContinuous(1000, REF_TONE_GAIN, "right");
    } else {
      // Tier 2 or 3: Use headphone profile + DAC estimate → finalize
      finalizeTieredCalibration();
    }
  };

  // Tier 1: User enters the dB reading from their phone
  const confirmDbMeterReading = () => {
    const spl = parseFloat(dbMeterReading);
    if (isNaN(spl) || spl < 30 || spl > 120) return; // invalid
    toneGen.current.stop();
    toneGen.current.setDbMeterCalibration(REF_TONE_GAIN, spl);
    setDeviceSetupStep("confirm");
  };

  // Finalize calibration for Tier 2/3
  const finalizeTieredCalibration = async () => {
    const phoneProfile = PHONE_DAC_PROFILES[detectedPhone] || PHONE_DAC_PROFILES.generic;
    const dacVrms = phoneProfile.output_vrms?.[TARGET_VOLUME_PERCENT] || 0.40;

    const ready = await toneGen.current.init();
    if (session?.headphone_type) toneGen.current.setTransducer(session.headphone_type);
    toneGen.current.startKeepAlive();

    if (calibrationTier === 2 && detectedHeadphone?.profile) {
      toneGen.current.setHeadphoneProfileCalibration(detectedHeadphone.profile, dacVrms);
    } else {
      toneGen.current.setStandardRangeCalibration(dacVrms);
    }
    setDeviceSetupStep("confirm");
  };

  // Final confirm → join session and start test
  const confirmDeviceSetup = () => {
    toneGen.current.stop();
    joinSession();
  };

  // ─── Legacy Calibration state (kept for fallback) ───
  const [calibSliderDb, setCalibSliderDb] = useState(-20);
  const calibToneRef = useRef(null);

  const startCalibration = async () => {
    setPhase(PHASES.CALIBRATION);
    const ready = await toneGen.current.init();
    setAudioReady(ready);
    if (session?.headphone_type) {
      toneGen.current.setTransducer(session.headphone_type);
    }
    toneGen.current.startKeepAlive();
    const gain = Math.pow(10, calibSliderDb / 20);
    await toneGen.current.playContinuous(1000, gain, "right");
  };

  const updateCalibTone = (dbFs) => {
    setCalibSliderDb(dbFs);
    const gain = Math.pow(10, dbFs / 20);
    toneGen.current.updateGain(gain);
  };

  const confirmCalibration = () => {
    const comfortableGain = Math.pow(10, calibSliderDb / 20);
    toneGen.current.setCalibration(comfortableGain);
    toneGen.current.stop();
    joinSession();
  };

  // ─── Join session ───
  const joinSession = async () => {
    setPhase(PHASES.JOINING);
    try {
      const ready = await toneGen.current.init();
      setAudioReady(ready);
      if (session?.headphone_type) {
        toneGen.current.setTransducer(session.headphone_type);
      }
      // Start silent keepalive to prevent AudioContext suspension on mobile
      toneGen.current.startKeepAlive();
      await axios.post(`${API_URL}/api/pta-live-sessions/${session.session_id || session.id}/patient-join`);
      const s = await fetchSession();
      if (s) {
        if (isSelfTest) {
          // Self-tests also need calibration — start AI test now.
          // Calibration was already done BEFORE joinSession was called.
          startAiTest();
        } else {
          setPhase(mapStatusToPhase(s.status));
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to join. Please try again.");
      setPhase(PHASES.ERROR);
    }
  };

  // ─── Auto-start AI test for self-tests ───
  const startAiTest = async () => {
    if (aiStarted) return;
    setAiStarted(true);
    try {
      const sessionId = session?.session_id || session?.id;
      await axios.post(`${API_URL}/api/public/hearing-test/${sessionId}/auto-start-ai`);
      setPhase(PHASES.TESTING);
    } catch (err) {
      console.error("AI test start error:", err);
      setError("Failed to start AI test. Please refresh and try again.");
      setPhase(PHASES.ERROR);
      setAiStarted(false);
    }
  };

  // ─── Download report ───
  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      const sessionId = session?.session_id || session?.id;
      // Try customer auth first, fall back to public endpoint
      const token = localStorage.getItem("hearclear_customer_token");
      let res;
      if (token) {
        try {
          res = await axios.get(`${API_URL}/api/customer/tests/${sessionId}/report`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });
        } catch {
          // Fallback to public endpoint
          res = await axios.get(`${API_URL}/api/public/hearing-test/${sessionId}/report`, {
            responseType: "blob",
          });
        }
      } else {
        res = await axios.get(`${API_URL}/api/public/hearing-test/${sessionId}/report`, {
          responseType: "blob",
        });
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `HearClear_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Report download failed. Please login to My Tests portal to download your report.");
    } finally {
      setDownloadingReport(false);
    }
  };

  // ─── Fetch AI Recommendation on completion ───
  const fetchAiRecommendation = useCallback(async () => {
    if (!session || loadingRec || aiRecommendation) return;
    setLoadingRec(true);
    try {
      const sessionId = session?.session_id || session?.id;
      const res = await axios.post(`${API_URL}/api/public/hearing-test/${sessionId}/ai-recommendation`);
      setAiRecommendation(res.data);
    } catch (err) {
      console.error("AI recommendation error:", err);
    } finally {
      setLoadingRec(false);
    }
  }, [session, loadingRec, aiRecommendation]);

  // Auto-fetch recommendation when entering completed phase
  useEffect(() => {
    if (phase === PHASES.COMPLETED) {
      // Stop keepalive when test finishes
      toneGen.current.stopKeepAlive();
      if (isSelfTest && !aiRecommendation && !loadingRec) {
        fetchAiRecommendation();
      }
    }
  }, [phase, isSelfTest, aiRecommendation, loadingRec, fetchAiRecommendation]);

  // ─── Keep AudioContext alive on mobile: resume on ANY touch/click ───
  useEffect(() => {
    if (phase !== PHASES.TESTING) return;
    const handler = () => { toneGen.current.ensureRunning(); };
    document.addEventListener("touchstart", handler, { passive: true });
    document.addEventListener("click", handler);
    return () => {
      document.removeEventListener("touchstart", handler);
      document.removeEventListener("click", handler);
    };
  }, [phase]);

  // ─── P0 FIX: Pause/Resume AI test on mobile interruptions ───
  // When phone call, notification, or app switch backgrounds the browser,
  // pause the backend AI test so it doesn't keep escalating volume.
  // Resume when the user returns to the tab.
  const isPausedByVisibility = useRef(false);
  useEffect(() => {
    if (phase !== PHASES.TESTING) return;
    const sessionId = session?.session_id || session?.id;
    if (!sessionId) return;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Browser backgrounded — pause AI test and suspend audio
        isPausedByVisibility.current = true;
        setTestPaused(true);
        toneGen.current.suspend();
        try {
          await axios.post(`${API_URL}/api/pta-live-sessions/${sessionId}/pause-ai-test`);
        } catch (e) {
          console.warn("Failed to pause AI test:", e);
        }
      } else if (isPausedByVisibility.current) {
        // Browser returned to foreground — resume AI test and audio
        isPausedByVisibility.current = false;
        setTestPaused(false);
        await toneGen.current.ensureRunning();
        try {
          await axios.post(`${API_URL}/api/pta-live-sessions/${sessionId}/resume-ai-test`);
        } catch (e) {
          console.warn("Failed to resume AI test:", e);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [phase, session]);

  // ─── P0 FIX: Full cleanup on component unmount (back button, navigation) ───
  // Stop all sounds when leaving the page. Don't close AudioContext in React cleanup
  // because StrictMode double-mounts in dev, which would destroy the context prematurely.
  useEffect(() => {
    const gen = toneGen.current;
    const poll = pollRef;
    return () => {
      gen.stop();
      gen.stopKeepAlive();
      stopNoiseCheck();
      if (poll.current) clearInterval(poll.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle browser close/navigate-away — fully destroy audio
  useEffect(() => {
    const handleBeforeUnload = () => {
      toneGen.current.destroy();
    };
    // Also clean up on popstate (back button navigation)
    const handlePopState = () => {
      toneGen.current.destroy();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // ─── Patient presses "I heard it" ───
  const lastResponseTimeRef = useRef(0);
  const handleResponse = async (e) => {
    // Prevent double-fire from touch + click on mobile (debounce 300ms)
    if (e) e.preventDefault();
    const now = Date.now();
    if (now - lastResponseTimeRef.current < 300) return;
    lastResponseTimeRef.current = now;
    // Always resume AudioContext on button press (user gesture)
    toneGen.current.ensureRunning();
    if (responseSubmittedRef.current || !currentToneId) return;

    const rt = toneStartRef.current ? Date.now() - toneStartRef.current : null;
    responseSubmittedRef.current = true;
    responseCountRef.current += 1;

    // Show brief "Recorded" flash
    setResponseFlash(true);
    setTimeout(() => setResponseFlash(false), 800);

    try {
      await axios.post(
        `${API_URL}/api/pta-live-sessions/${session.session_id}/patient-response?tone_id=${currentToneId}&response_time_ms=${rt || 0}`
      );
    } catch (err) {
      console.error("Response error:", err);
      // On failure, allow retry
      responseSubmittedRef.current = false;
    }
  };

  // ─── Calibration (audiologist-controlled sessions only, NOT self-tests) ───
  const isRightEar = session?.status === "testing_right" || session?.current_ear === "right";

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  // Loading
  if (phase === PHASES.LOADING) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-gray-600 text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>Connecting to your hearing test...</p>
        </div>
      </div>
    );
  }

  // Error
  if (phase === PHASES.ERROR) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error || "We couldn't connect to your test session."}</p>
          <button onClick={() => window.location.reload()} data-testid="retry-btn"
            className="bg-blue-800 hover:bg-blue-900 text-white font-semibold px-8 py-3 rounded-full">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Welcome
  if (phase === PHASES.WELCOME) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full" data-testid="patient-welcome">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ background: BRAND_COLOR, boxShadow: `0 8px 24px ${BRAND_COLOR}40` }}>
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>HearClear</h1>
            <p className="font-medium mt-1" style={{ color: BRAND_COLOR }}>{isSelfTest ? "AI-Powered Hearing Test" : "Hearing Assessment"}</p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Welcome, {session?.patient_name}!</h2>
            <p className="text-gray-500 text-center text-sm mb-6">
              {isSelfTest
                ? "Your AI-powered hearing assessment is ready. The entire test is fully automated."
                : <>Your audiologist <strong className="text-gray-700">{session?.audiologist_name}</strong> is ready to conduct your hearing test.</>
              }
            </p>
            <div className="bg-amber-50 rounded-2xl p-5 mb-6 border border-amber-100">
              <h3 className="font-bold text-amber-800 text-sm mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Before you begin, please ensure:
              </h3>
              <ul className="space-y-3">
                {[
                  { icon: "headphones", text: "For best results, use wired earphones or headphones" },
                  { icon: "volume", text: "You are in a quiet room with no background noise" },
                  { icon: "phone", text: "Put your phone on silent mode to avoid interruptions during the test" },
                  { icon: "speaker", text: "Your device volume is turned up to maximum" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-amber-900">
                    <svg className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button onClick={startHeadphoneCheck} data-testid="join-session-btn"
              className="w-full text-white font-bold py-4 rounded-2xl text-lg transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
              style={{ background: BRAND_COLOR, boxShadow: `0 8px 24px ${BRAND_COLOR}30` }}>
              I'm Ready — {isSelfTest ? "Start Test" : "Join Test"}
            </button>
            <p className="text-center text-xs text-gray-400 mt-4">This test takes approximately 8-10 minutes</p>
          </div>
        </div>
      </div>
    );
  }

  // Headphone Check — Ensure headphones/earphones are connected before proceeding
  if (phase === PHASES.HEADPHONE_CHECK) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full" data-testid="headphone-check">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Connect Your Headphones</h2>
            <p className="text-gray-500 text-sm mb-6">
              This test requires wired earphones, headphones, or AirPods. <strong className="text-gray-700">Speaker audio cannot be used</strong> for accurate hearing assessment.
            </p>

            {headphoneStatus === "checking" && (
              <div className="flex items-center justify-center gap-2 text-indigo-600 mb-4">
                <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Detecting audio devices...</span>
              </div>
            )}

            {headphoneStatus === "detected" && (
              <div className="space-y-4">
                <div className="bg-green-50 rounded-2xl p-5 border border-green-100">
                  <div className="flex items-center justify-center gap-2 text-green-700 mb-2">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    <span className="font-bold text-sm">Headphones Detected</span>
                  </div>
                  {headphoneDeviceName && (
                    <p className="text-green-600 text-xs">{headphoneDeviceName}</p>
                  )}
                </div>
                <button
                  onClick={startNoiseCheck}
                  data-testid="headphone-continue-btn"
                  className="w-full text-white font-bold py-4 rounded-2xl text-lg transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
                  style={{ background: "#16a34a", boxShadow: "0 8px 24px rgba(22,163,74,0.3)" }}
                >
                  Continue
                </button>
              </div>
            )}

            {headphoneStatus === "not_detected" && (
              <div className="space-y-4">
                <div className="bg-red-50 rounded-2xl p-5 border border-red-200">
                  <div className="flex items-center justify-center gap-2 text-red-700 mb-2">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                    <span className="font-bold text-sm">No Headphones Detected</span>
                  </div>
                  <p className="text-red-600 text-xs mt-1">
                    Please connect wired earphones, headphones, or Bluetooth earbuds (AirPods, etc.) and try again.
                  </p>
                </div>

                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 text-left">
                  <p className="text-amber-800 text-xs font-medium mb-2">Why headphones are required:</p>
                  <ul className="text-amber-700 text-xs space-y-1">
                    <li>- Each ear must be tested separately</li>
                    <li>- Speaker output is not calibrated for hearing tests</li>
                    <li>- Background noise will interfere with results</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={startHeadphoneCheck}
                    data-testid="headphone-recheck-btn"
                    className="flex-1 py-3 rounded-xl font-bold text-sm border-2 transition-colors"
                    style={{ borderColor: BRAND_COLOR, color: BRAND_COLOR }}
                  >
                    Re-check
                  </button>
                  <button
                    onClick={startNoiseCheck}
                    data-testid="headphone-proceed-btn"
                    className="flex-1 text-white py-3 rounded-xl font-bold text-sm transition-colors opacity-80"
                    style={{ background: "#6b7280" }}
                  >
                    I Have Headphones On
                  </button>
                </div>
                <p className="text-gray-400 text-xs">If your headphones are connected but not detected, tap "I Have Headphones On" to continue.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Noise Check
  if (phase === PHASES.NOISE_CHECK) {
    const noisePercent = noiseLevel ? Math.min(100, Math.round((noiseLevel / 80) * 100)) : 0;
    const barColor = noiseStatus === "pass" ? "#22c55e" : noiseStatus === "fail" ? "#ef4444" : BRAND_COLOR;
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full" data-testid="noise-check">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Checking Ambient Noise</h2>
            <p className="text-gray-500 text-sm mb-6">
              {noiseStatus === "checking" && "Measuring background noise level..."}
              {noiseStatus === "pass" && "Environment is quiet enough for testing."}
              {noiseStatus === "fail" && "Background noise is too high for accurate results."}
            </p>

            {/* Noise level meter */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Quiet</span>
                <span>{noiseLevel !== null ? `${noiseLevel} dB` : "..."}</span>
                <span>Loud</span>
              </div>
              <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${noisePercent}%`, background: barColor }}
                />
              </div>
              <div className="relative mt-1">
                <div className="absolute text-xs text-gray-400" style={{ left: `${Math.round((NOISE_THRESHOLD_DB / 80) * 100)}%`, transform: "translateX(-50%)" }}>
                  {NOISE_THRESHOLD_DB} dB limit
                </div>
              </div>
            </div>

            {noiseStatus === "checking" && (
              <div className="flex items-center justify-center gap-2 text-blue-600">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Listening...</span>
              </div>
            )}
            {noiseStatus === "pass" && (
              <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <span className="font-bold">Environment OK — Starting test...</span>
              </div>
            )}
            {noiseStatus === "fail" && (
              <div className="space-y-3">
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <p className="text-red-800 text-sm font-medium">Please move to a quieter location for accurate results.</p>
                  <p className="text-red-600 text-xs mt-1">High background noise can mask test tones and give incorrect results.</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={startNoiseCheck}
                    data-testid="noise-retry-btn"
                    className="flex-1 py-3 rounded-xl font-bold text-sm border-2 transition-colors"
                    style={{ borderColor: BRAND_COLOR, color: BRAND_COLOR }}
                  >
                    Re-check Noise
                  </button>
                  <button
                    onClick={() => { stopNoiseCheck(); startCalibration(); }}
                    data-testid="noise-proceed-btn"
                    className="flex-1 text-white py-3 rounded-xl font-bold text-sm transition-colors"
                    style={{ background: BRAND_COLOR }}
                  >
                    Proceed Anyway
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Joining
  if (phase === PHASES.JOINING) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-gray-700 text-lg font-medium">Joining your test session...</p>
        </div>
      </div>
    );
  }

  // Waiting
  if (phase === PHASES.WAITING) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center" data-testid="patient-waiting">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>You're Connected!</h2>
          <p className="text-gray-600 mb-6">
            Please wait while your audiologist prepares the test. Keep your headphones on.
          </p>
          <div className="flex justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
          <p className="text-sm text-gray-400">Audiologist: {session?.audiologist_name}</p>
        </div>
      </div>
    );
  }

  // Instructions
  if (phase === PHASES.INSTRUCTIONS) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white p-6" data-testid="patient-instructions">
        <div className="max-w-lg mx-auto pt-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>How This Test Works</h2>
            <p className="text-gray-500 mt-2">Please read carefully before we begin</p>
          </div>
          <div className="space-y-5">
            {[
              { num: "1", title: "You will hear soft beeping sounds", desc: "We'll play tones at different pitches through your headphones. Some sounds will be very soft — that's normal. Listen carefully.", color: "bg-blue-700" },
              { num: "2", title: "Press the button when you hear a sound", desc: "A large button will appear on your screen. Tap it every time you hear a beep — even if the sound is very faint.", color: "bg-emerald-500" },
              { num: "3", title: "We test one ear at a time", desc: "First we'll test your right ear, then your left ear. Keep both sides of your headphones on.", color: "bg-sky-500" },
              { num: "4", title: "Only press when you actually hear something", desc: "It's perfectly okay to miss some sounds — don't guess. Only press the button when you truly hear a beep.", color: "bg-amber-500" },
            ].map((step) => (
              <div key={step.num} className="flex gap-4 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className={`w-10 h-10 ${step.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white font-bold text-lg">{step.num}</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base">{step.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <div className="flex justify-center gap-2 mb-3">
              <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <p className="text-gray-400 text-sm">Waiting for your audiologist to continue...</p>
          </div>
        </div>
      </div>
    );
  }

  // ═══ DEVICE SETUP — Tiered Calibration ═══
  if (phase === PHASES.DEVICE_SETUP) {
    const phoneProfile = PHONE_DAC_PROFILES[detectedPhone] || PHONE_DAC_PROFILES.generic;
    const phoneName = phoneProfile.name;
    const hpName = detectedHeadphone?.profile?.name || detectedHeadphone?.label || null;
    const tierLabel = calibrationTier === 1 ? "High Accuracy" : calibrationTier === 2 ? "Calibrated" : "Standard";
    const tierColor = calibrationTier === 1 ? "#16a34a" : calibrationTier === 2 ? "#2563eb" : "#7c3aed";

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6" data-testid="device-setup">
        <div className="max-w-md mx-auto pt-4">
          <div className="text-center mb-5">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${tierColor}15` }}>
              <svg className="w-8 h-8" style={{ color: tierColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Device Calibration</h2>
            <div className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: tierColor }}>
              {tierLabel} Mode
            </div>
          </div>

          {/* Device Detection Summary */}
          <div className="bg-white rounded-2xl p-4 mb-4 border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Detected Devices</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{phoneName}</p>
                  <p className="text-xs text-gray-500">{phoneProfile.has_db_meter ? "Built-in dB meter available" : "Standard output"}</p>
                </div>
                <svg className="w-5 h-5 text-green-500 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{hpName || "Headphones"}</p>
                  <p className="text-xs text-gray-500">{detectedHeadphone?.profile ? "Profile matched" : "Standard profile"}</p>
                </div>
                <svg className={`w-5 h-5 ml-auto ${detectedHeadphone?.profile ? "text-green-500" : "text-amber-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={detectedHeadphone?.profile ? "M5 13l4 4L19 7" : "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"} /></svg>
              </div>
            </div>
          </div>

          {/* STEP: Set Volume */}
          {deviceSetupStep === "volume_set" && (
            <div className="bg-amber-50 rounded-2xl p-5 mb-5 border border-amber-100">
              <h3 className="font-bold text-amber-800 text-sm mb-2">
                Set your phone volume to {TARGET_VOLUME_PERCENT}%
              </h3>
              <p className="text-amber-700 text-xs mb-3">
                Use the physical volume buttons on your phone. Set it to approximately {TARGET_VOLUME_PERCENT}% — this is important for test accuracy.
              </p>
              <div className="flex items-center justify-center gap-1 my-3">
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-sm transition-all"
                    style={{
                      width: 8, height: 12 + i * 3,
                      background: i < TARGET_VOLUME_PERCENT / 10 ? "#f59e0b" : "#e5e7eb",
                    }}
                  />
                ))}
              </div>
              <p className="text-amber-600 text-xs text-center font-medium">
                {TARGET_VOLUME_PERCENT / 10} out of 10 bars
              </p>
              <button
                onClick={confirmVolumeSet}
                data-testid="volume-set-confirm-btn"
                className="w-full mt-4 text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-[0.98]"
                style={{ background: tierColor }}
              >
                Volume Set — Continue
              </button>
            </div>
          )}

          {/* STEP: dB Meter Reading (Tier 1 only) */}
          {deviceSetupStep === "db_meter" && calibrationTier === 1 && (
            <div className="bg-green-50 rounded-2xl p-5 mb-5 border border-green-100">
              <h3 className="font-bold text-green-800 text-sm mb-2">Read Your Phone's dB Meter</h3>
              <p className="text-green-700 text-xs mb-3">
                A tone is playing. Open your phone's headphone level meter and enter the dB reading below.
              </p>
              {detectedPhone === "iphone" ? (
                <div className="bg-white rounded-xl p-3 mb-3 border border-green-200 text-xs text-gray-600">
                  <p className="font-bold text-gray-800 mb-1">iPhone:</p>
                  <p>Open <strong>Control Center</strong> (swipe down from top-right corner) → Look for the <strong>Hearing</strong> icon (ear icon) → Read the <strong>Headphone Level</strong> in dB</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-3 mb-3 border border-green-200 text-xs text-gray-600">
                  <p className="font-bold text-gray-800 mb-1">Samsung:</p>
                  <p>Open <strong>Settings</strong> → <strong>Digital Wellbeing</strong> → <strong>Volume Monitor</strong> → Read the current <strong>headphone volume</strong> in dB</p>
                </div>
              )}
              <div className="flex items-center gap-3 mt-3">
                <input
                  type="number"
                  placeholder="e.g. 71"
                  value={dbMeterReading}
                  onChange={(e) => setDbMeterReading(e.target.value)}
                  className="flex-1 border border-green-300 rounded-xl px-4 py-3 text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-green-400"
                  data-testid="db-meter-input"
                />
                <span className="text-green-800 font-bold text-sm">dB</span>
              </div>
              <button
                onClick={confirmDbMeterReading}
                disabled={!dbMeterReading || isNaN(parseFloat(dbMeterReading))}
                data-testid="db-meter-confirm-btn"
                className="w-full mt-4 text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-[0.98] disabled:opacity-50"
                style={{ background: "#16a34a" }}
              >
                Confirm Reading
              </button>
            </div>
          )}

          {/* STEP: Confirmation */}
          {deviceSetupStep === "confirm" && (
            <div className="bg-white rounded-2xl p-5 mb-5 border-2 shadow-sm" style={{ borderColor: tierColor }}>
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-6 h-6" style={{ color: tierColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <h3 className="font-bold text-gray-900 text-sm">Calibration Complete</h3>
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <p>Phone: <strong>{phoneName}</strong></p>
                <p>Headphones: <strong>{hpName || "Standard"}</strong></p>
                <p>Mode: <strong style={{ color: tierColor }}>{tierLabel}</strong></p>
                {calibrationTier === 1 && dbMeterReading && (
                  <p>Measured: <strong>{dbMeterReading} dB SPL</strong></p>
                )}
              </div>
              <button
                onClick={confirmDeviceSetup}
                data-testid="device-setup-confirm-btn"
                className="w-full mt-4 text-white font-bold py-4 rounded-xl text-lg transition-all active:scale-[0.98] shadow-lg"
                style={{ background: tierColor, boxShadow: `0 8px 24px ${tierColor}40` }}
              >
                Start Hearing Test
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Calibration — Legacy comfortable-level volume calibration (fallback)
  if (phase === PHASES.CALIBRATION) {
    const sliderPercent = ((calibSliderDb - (-50)) / ((-5) - (-50))) * 100;
    // Compute the maximum testable dB HL based on current slider position
    const comfortGain = Math.pow(10, calibSliderDb / 20);
    const maxTestableDb = comfortGain > 0 ? Math.round(40 + 20 * Math.log10(1.0 / comfortGain)) : 0;
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white p-6" data-testid="patient-calibration">
        <div className="max-w-md mx-auto pt-4">
          <div className="text-center mb-5">
            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12V4.5" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Volume Calibration</h2>
            <p className="text-gray-500 text-sm mt-1">This step ensures accurate test results for YOUR device</p>
          </div>

          {/* Step 1: Volume instruction */}
          <div className="bg-amber-50 rounded-2xl p-5 mb-5 border border-amber-100">
            <h3 className="font-bold text-amber-800 text-sm mb-1">Step 1: Set phone volume to MAXIMUM</h3>
            <p className="text-amber-700 text-xs">Use the physical volume buttons on your phone. This is important for accuracy.</p>
          </div>

          {/* Step 2: Slider calibration — comfortable level */}
          <div className="bg-white rounded-2xl p-5 mb-5 border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Step 2: Set a comfortable listening level</h3>
            <p className="text-gray-500 text-xs mb-4">A tone is playing in your RIGHT ear. Adjust the slider until the tone sounds <strong className="text-gray-700">clearly audible and comfortable</strong> — like a normal conversation, not too loud and not too soft.</p>

            {/* Volume slider */}
            <div className="relative mb-3">
              <div className="flex justify-between text-xs text-gray-400 mb-2">
                <span>Louder</span>
                <span>Comfortable</span>
                <span>Softer</span>
              </div>
              <input
                type="range"
                min={-50}
                max={-5}
                step={1}
                value={calibSliderDb}
                onChange={(e) => updateCalibTone(Number(e.target.value))}
                className="w-full h-3 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #7c3aed ${sliderPercent}%, #e5e7eb ${sliderPercent}%)`,
                  accentColor: "#7c3aed",
                }}
                data-testid="calibration-slider"
              />
            </div>

            {/* Dynamic range indicator — shows max testable dB */}
            <div className="bg-gray-50 rounded-xl p-3 mt-3 border border-gray-100">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Test Range:</span>
                <span className="font-bold" style={{ color: maxTestableDb >= 80 ? "#16a34a" : maxTestableDb >= 60 ? "#ca8a04" : "#ef4444" }} data-testid="max-testable-db">
                  0 — {Math.min(maxTestableDb, 120)} dB HL
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-200"
                  style={{
                    width: `${Math.min(100, (maxTestableDb / 120) * 100)}%`,
                    background: maxTestableDb >= 80 ? "#16a34a" : maxTestableDb >= 60 ? "#ca8a04" : "#ef4444",
                  }}
                />
              </div>
              <p className="text-gray-400 mt-1" style={{ fontSize: "10px" }}>
                {maxTestableDb >= 80
                  ? "Excellent range — suitable for mild to severe hearing loss"
                  : maxTestableDb >= 60
                    ? "Good range — suitable for mild to moderate hearing loss"
                    : "Limited range — lower the comfortable level for better accuracy"}
              </p>
            </div>
          </div>

          {/* Confirm */}
          <button
            onClick={confirmCalibration}
            data-testid="calibration-confirm-btn"
            className="w-full text-white font-bold py-4 rounded-2xl text-lg transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
            style={{ background: "#7c3aed", boxShadow: "0 8px 24px rgba(124,58,237,0.3)" }}
          >
            This Sounds Comfortable — Continue
          </button>
          <p className="text-gray-400 text-xs mt-3 text-center">The tone should sound like a normal conversation — clear but not loud</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // TESTING PHASE — THE CORE SCREEN
  // CRITICAL: Button NEVER changes appearance based on tone state.
  // The button is ALWAYS identical — same color, same text, same size.
  // The patient must rely ONLY on hearing to detect tones.
  // ═══════════════════════════════════════
  if (phase === PHASES.TESTING) {
    const earLabel = isRightEar ? "RIGHT" : "LEFT";
    const thresholds = session?.thresholds || { left: {}, right: {} };
    const hasThresholdData = Object.keys(thresholds.right || {}).length > 0 || Object.keys(thresholds.left || {}).length > 0;

    return (
      <div className="min-h-screen bg-gray-900 flex flex-col relative" data-testid="patient-testing">
        {/* Top ear indicator — static, neutral */}
        <div className="px-4 py-3 text-center" style={{ background: BRAND_COLOR }}>
          <div className="flex items-center justify-center gap-2 text-white">
            <svg className={`w-5 h-5 ${!isRightEar ? "scale-x-[-1]" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>
            <span className="font-bold text-lg tracking-wide">Testing {earLabel} Ear</span>
          </div>
        </div>

        {/* Instruction */}
        <div className="px-6 pt-5 pb-2 text-center">
          <p className="text-gray-300 text-lg font-medium">
            Press the button below <strong className="text-white">every time you hear a beep</strong>
          </p>
          <p className="text-gray-500 text-sm mt-1">Even if the sound is very faint — do not guess</p>
        </div>

        {/* Paused overlay — shown when app was backgrounded (phone call, etc.) */}
        {testPaused && (
          <div className="absolute inset-0 z-50 bg-gray-900/95 flex items-center justify-center" data-testid="test-paused-overlay">
            <div className="text-center p-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Test Paused</h2>
              <p className="text-gray-400 text-sm mb-6">Your test was paused because you left the app. Resuming now...</p>
              <div className="flex items-center justify-center gap-2 text-amber-400">
                <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium">Reconnecting...</span>
              </div>
            </div>
          </div>
        )}

        {/* Response button — ALWAYS IDENTICAL, NEVER changes appearance */}
        {/* Uses onTouchEnd for reliable mobile response + onClick for desktop */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="relative">
            <button
              onTouchEnd={handleResponse}
              onClick={handleResponse}
              data-testid="response-button"
              className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-full flex flex-col items-center justify-center select-none shadow-2xl active:scale-95 transition-transform duration-100 touch-manipulation"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLOR}, #2a5098)`,
                boxShadow: `0 0 60px ${BRAND_COLOR}40, 0 20px 60px rgba(0,0,0,0.3)`,
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
              }}
            >
              <svg className="w-12 h-12 sm:w-16 sm:h-16 text-white/90 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
              </svg>
              <span className="text-white text-xl sm:text-2xl font-bold">I HEARD IT</span>
              <span className="text-white/50 text-xs mt-1">tap when you hear a sound</span>
            </button>
            {/* Response recorded confirmation */}
            {responseFlash && (
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-green-500/90 text-white text-sm font-semibold px-4 py-1.5 rounded-full animate-pulse" data-testid="response-flash">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path d="M5 13l4 4L19 7"/></svg>
                Response Recorded
              </div>
            )}
          </div>
        </div>

        {/* Live Audiogram — builds in real-time as thresholds are found */}
        {hasThresholdData && (
          <div className="px-4 pb-4">
            <div className="bg-gray-800/80 rounded-xl p-3 border border-gray-700">
              <p className="text-gray-400 text-xs text-center mb-2 font-medium">Your Audiogram (building live...)</p>
              <MiniPatientAudiogram thresholds={thresholds} />
            </div>
          </div>
        )}

        {/* Bottom */}
        <div className="px-6 pb-6 text-center">
          <p className="text-gray-500 text-sm">
            Stay focused and listen carefully. Some sounds will be very quiet.
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // COMPLETED
  // ═══════════════════════════════════════
  if (phase === PHASES.COMPLETED) {
    const ptaRight = session?.pta_right;
    const ptaLeft = session?.pta_left;
    const sevRight = session?.severity_right;
    const sevLeft = session?.severity_left;
    const isNormalHearing = sevRight === "Normal" && sevLeft === "Normal";
    const maxPta = Math.max(ptaRight || 0, ptaLeft || 0);
    const thresholds = session?.thresholds || { left: {}, right: {} };
    const hasThresholdData = Object.keys(thresholds.right || {}).length > 0 || Object.keys(thresholds.left || {}).length > 0;

    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-sky-50 p-4 sm:p-6" data-testid="patient-completed">
        <div className="max-w-lg mx-auto pt-4 sm:pt-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className={`w-24 h-24 ${isNormalHearing ? "bg-emerald-100" : "bg-sky-100"} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {isNormalHearing ? (
                <svg className="w-12 h-12 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              ) : (
                <svg className="w-12 h-12 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              )}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Test Complete!</h2>
            <p className="text-gray-600">Thank you, <strong>{session?.patient_name}</strong>!</p>
          </div>

          {/* Normal Hearing — Congratulations Message */}
          {isNormalHearing && (
            <div className="bg-emerald-50 rounded-2xl p-6 mb-5 border border-emerald-200">
              <h3 className="text-xl font-bold text-emerald-800 mb-3 text-center">Great News! Your Hearing is Normal</h3>
              <p className="text-emerald-700 text-sm leading-relaxed mb-3">
                Congratulations! Your hearing thresholds are within normal limits for both ears. This is excellent news — 
                you should have no difficulty hearing in everyday situations like conversations, phone calls, and environmental sounds.
              </p>
              <div className="bg-white/60 rounded-xl p-4 mt-3">
                <p className="text-emerald-800 text-xs font-bold mb-2">Keep Your Hearing Healthy:</p>
                <ul className="text-emerald-700 text-xs space-y-1.5">
                  <li>- Get your hearing checked every 12–24 months</li>
                  <li>- Use earplugs at concerts and loud events</li>
                  <li>- Follow the 60/60 rule: max 60% volume for 60 minutes with earphones</li>
                  <li>- Stay active — cardiovascular exercise supports healthy hearing</li>
                </ul>
              </div>
              <p className="text-emerald-600 text-xs mt-3 font-medium text-center">No hearing aids or intervention needed at this time.</p>
            </div>
          )}

          {/* Hearing Loss — Detailed Diagnosis & Counselling */}
          {!isNormalHearing && (ptaLeft != null || ptaRight != null) && (
            <div className="bg-amber-50 rounded-2xl p-5 mb-4 border border-amber-200">
              <h3 className="font-bold text-amber-900 text-sm mb-3">Clinical Assessment</h3>
              <p className="text-amber-800 text-sm leading-relaxed mb-3">
                Your results indicate <strong>{sevRight !== "Normal" ? `${sevRight?.toLowerCase()} hearing loss in the right ear` : "normal hearing in the right ear"}</strong> and <strong>{sevLeft !== "Normal" ? `${sevLeft?.toLowerCase()} hearing loss in the left ear` : "normal hearing in the left ear"}</strong>.
              </p>
              <div className="bg-white/60 rounded-xl p-4 mb-3">
                <p className="text-amber-900 text-xs font-bold mb-2">What this may mean for daily life:</p>
                <ul className="text-amber-800 text-xs space-y-1.5">
                  {(maxPta <= 40) ? (
                    <>
                      <li>- Difficulty hearing soft speech or whispers</li>
                      <li>- Challenges in noisy environments (restaurants, gatherings)</li>
                      <li>- Needing to ask people to repeat themselves</li>
                      <li>- Turning up TV/phone volume higher than others prefer</li>
                    </>
                  ) : (maxPta <= 55) ? (
                    <>
                      <li>- Difficulty understanding conversational speech</li>
                      <li>- Significant challenges in group discussions</li>
                      <li>- Frequently misunderstanding words (especially s, f, th, sh sounds)</li>
                      <li>- Listening fatigue throughout the day</li>
                    </>
                  ) : (
                    <>
                      <li>- Only loud speech close to you may be heard clearly</li>
                      <li>- Phone conversations are very difficult without amplification</li>
                      <li>- Heavy reliance on lip-reading and visual cues</li>
                      <li>- Important alerts and warning sounds may be missed</li>
                    </>
                  )}
                </ul>
              </div>
              <div className="bg-white/60 rounded-xl p-4">
                <p className="text-amber-900 text-xs font-bold mb-2">Recommended Next Steps:</p>
                <ul className="text-amber-800 text-xs space-y-1.5">
                  <li>- Schedule a comprehensive hearing aid consultation</li>
                  <li>- Consider a hearing aid trial — modern devices are discreet and effective</li>
                  <li>- Face speakers directly and reduce background noise in conversations</li>
                  <li>- Protect remaining hearing with earplugs in loud environments</li>
                </ul>
              </div>
            </div>
          )}

          {/* Results Summary */}
          {(ptaLeft != null || ptaRight != null) && (
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-center mb-3 text-sm">Your Hearing Results</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className={`${sevRight === "Normal" ? "bg-emerald-50 border-emerald-100" : "bg-red-50 border-red-100"} rounded-xl p-4 text-center border`}>
                  <p className="text-xs text-gray-500 mb-1">Right Ear</p>
                  <p className="text-2xl font-bold text-gray-900">{ptaRight ?? "N/A"} <span className="text-sm font-normal">dB</span></p>
                  <p className={`text-xs font-semibold mt-1 ${
                    sevRight === "Normal" ? "text-emerald-600" : sevRight === "Mild" ? "text-yellow-600" : "text-red-600"
                  }`}>{sevRight || "N/A"}</p>
                </div>
                <div className={`${sevLeft === "Normal" ? "bg-emerald-50 border-emerald-100" : "bg-blue-50 border-blue-100"} rounded-xl p-4 text-center border`}>
                  <p className="text-xs text-gray-500 mb-1">Left Ear</p>
                  <p className="text-2xl font-bold text-gray-900">{ptaLeft ?? "N/A"} <span className="text-sm font-normal">dB</span></p>
                  <p className={`text-xs font-semibold mt-1 ${
                    sevLeft === "Normal" ? "text-emerald-600" : sevLeft === "Mild" ? "text-yellow-600" : "text-red-600"
                  }`}>{sevLeft || "N/A"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Live Audiogram */}
          {hasThresholdData && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-center text-sm mb-2">Your Audiogram</h3>
              <MiniPatientAudiogram thresholds={thresholds} light />
            </div>
          )}

          {/* AI Recommendation — Show for all test types */}
          {(
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                AI Clinical Recommendation
              </h3>
              {loadingRec ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin mr-3" />
                  <p className="text-gray-500 text-sm">Generating your personalized recommendation...</p>
                </div>
              ) : aiRecommendation ? (
                <div>
                  <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {aiRecommendation.ai_recommendation}
                  </div>
                  {/* Hearing Aid Suggestions */}
                  {aiRecommendation.needs_intervention && aiRecommendation.hearing_aid_suggestions?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-bold text-gray-900 text-xs mb-3">Recommended Hearing Aids for You</h4>
                      <div className="space-y-2">
                        {aiRecommendation.hearing_aid_suggestions.map((aid, i) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">{aid.name}</p>
                              <p className="text-xs text-gray-500">{aid.brand} • {aid.form_factor}</p>
                            </div>
                            {aid.price && <p className="font-bold text-gray-900 text-sm">&#x20B9;{aid.price?.toLocaleString()}</p>}
                          </div>
                        ))}
                      </div>
                      <a href="https://hearclear.in/products" target="_blank" rel="noreferrer"
                        className="block mt-3 text-center text-sm font-semibold text-violet-700 hover:text-violet-800">
                        View All Hearing Aids
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Recommendation will appear here shortly...</p>
              )}
            </div>
          )}

          {/* Hearing Loss Classification Reference */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
            <h3 className="font-bold text-gray-900 text-sm mb-3">Hearing Loss Classification (WHO)</h3>
            <div className="divide-y divide-gray-100">
              {[
                { degree: "Normal", range: "0–25 dB", color: "bg-emerald-100 text-emerald-700" },
                { degree: "Mild", range: "26–40 dB", color: "bg-yellow-100 text-yellow-700" },
                { degree: "Moderate", range: "41–55 dB", color: "bg-orange-100 text-orange-700" },
                { degree: "Mod. Severe", range: "56–70 dB", color: "bg-red-100 text-red-700" },
                { degree: "Severe", range: "71–90 dB", color: "bg-red-200 text-red-800" },
                { degree: "Profound", range: "91+ dB", color: "bg-red-300 text-red-900" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${item.color}`}>{item.degree}</span>
                  <span className="text-xs text-gray-500">{item.range}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
            <p className="text-gray-400 text-xs leading-relaxed">
              <strong>Disclaimer:</strong> This hearing assessment was conducted using an AI-powered screening tool on a consumer device. 
              Results are for screening purposes and may differ from clinical audiometry. Please consult a qualified audiologist for a 
              comprehensive evaluation and treatment plan.
            </p>
          </div>

          {/* Actions */}
          {isSelfTest ? (
            <div className="space-y-3">
              <button onClick={downloadReport} disabled={downloadingReport} data-testid="download-report-btn"
                className="w-full text-white font-bold py-4 rounded-2xl text-lg transition-all shadow-lg disabled:bg-gray-300 active:scale-[0.98]"
                style={downloadingReport ? {} : { background: BRAND_COLOR, boxShadow: `0 8px 24px ${BRAND_COLOR}30` }}>
                {downloadingReport ? "Downloading..." : "Download Your Report (PDF)"}
              </button>
              {!isNormalHearing && (
                <a href="https://hearclear.in/products" target="_blank" rel="noreferrer" data-testid="shop-link"
                  className="block w-full bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 font-bold py-4 rounded-2xl text-center transition-all">
                  Browse Hearing Aids on HearClear
                </a>
              )}
            </div>
          ) : (
            <div>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed text-center">
                Your audiologist will review the results and discuss them with you shortly.
              </p>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Test conducted by</p>
                <p className="font-bold text-gray-900">{session?.audiologist_name}</p>
                <p className="text-xs text-gray-400 mt-1">HearClear Audiology</p>
              </div>
            </div>
          )}
          <p className="text-center text-xs text-gray-400 mt-6 pb-4">Your results have been saved securely to your account.</p>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center">
        <p className="text-gray-500">Session status: {session?.status}</p>
        <p className="text-gray-400 text-sm mt-2">Please wait...</p>
      </div>
    </div>
  );
}
